import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { changePassword } from "../../../api/UserAPIUtils";
import { useAppSelector } from "../../../hooks/useAppSelector";
import LoadingSpinner from "../../../components/ui/feedback/LoadingSpinner";
import { useAppDispatch } from "../../../hooks/useAppDispatch";
import { passwordChanged } from "../../auth/authSlice";
import { AxiosError } from "axios";
import AuthShell from "./AuthShell";

const ChangePasswordWindow: React.FC = () => {
  const userId = useAppSelector((state) => state.user.id);
  const role = useAppSelector((state) => state.user.role?.toLocaleLowerCase());
  const [loading, setLoading] = useState(false);
  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmNewPassword, setConfirmNewPassword] = useState("");
  const [error, setError] = useState("");
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setNewPassword(e.target.value);
    if (e.target.value !== confirmNewPassword) {
      setError("Passwords do not match");
    } else if (e.target.value.length < 8) {
      setError("Password must be at least 8 characters long");
    } else {
      setError("");
    }
  };

  const handleConfirmPasswordChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setConfirmNewPassword(e.target.value);
    if (e.target.value !== newPassword) {
      setError("Passwords do not match");
    } else if (e.target.value.length < 8) {
      setError(
        "Password must be at least 8 characters, include 1 number, 1 special character, and 1 uppercase letter.",
      );
    } else {
      setError("");
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (newPassword !== confirmNewPassword) {
      setError("Passwords do not match");
      return;
    }
    const passwordRegex =
      /^(?=.*\d)(?=.*[!@#$%^&*()_+\[\]{}|;:,.<>?])[A-Za-z\d!@#$%^&*()_+\[\]{}|;:,.<>?]{8,}$/;
    if (!passwordRegex.test(newPassword)) {
      setError(
        "Password must be at least 8 characters, include 1 number, 1 special character, and 1 uppercase letter.",
      );
      return;
    }
    // Call the reset password API
    if (userId === null) {
      setError("User ID is invalid");
      return;
    }
    setLoading(true);

    try {
      const response = await changePassword(oldPassword, newPassword, userId);
      if (response.success) {
        dispatch(passwordChanged());
        navigate("/");
      } else {
      }
    } catch (error) {
      if (error instanceof AxiosError) {
        const errorMessage =
          error.response?.data?.message || "An unexpected error occurred.";
        setError(errorMessage);
      } else {
        setError("An unexpected error occurred.");
      }
      console.error("Failed to create new user", error);
    }
    setLoading(false);
  };

  // Students use a platform-managed yearly password \u2013 redirect them home
  // if they somehow reach this window (the backend already prevents this).
  if (role === "student") {
    dispatch(passwordChanged());
    navigate("/");
    return null;
  }

  return (
    <>
      {loading && <LoadingSpinner />}
      <AuthShell
        eyebrow="Security"
        title="Change password"
        description="Set a strong new password to secure your account before continuing."
      >
        <form onSubmit={handleSubmit} className="space-y-5">
          <div>
            <label className="mb-2 block text-[11px] font-bold uppercase tracking-[0.22em] text-slate-700">
              Old password
            </label>
            <input
              type="password"
              value={oldPassword}
              onChange={(e) => setOldPassword(e.target.value)}
              required
              className="h-14 w-full rounded-[20px] border border-slate-200 bg-white px-5 text-base text-slate-700 outline-none transition focus:border-primary focus:ring-2 focus:ring-primary/15"
              placeholder="Enter your current password"
            />
          </div>

          <div>
            <label className="mb-2 block text-[11px] font-bold uppercase tracking-[0.22em] text-slate-700">
              New password
            </label>
            <input
              type="password"
              value={newPassword}
              onChange={handlePasswordChange}
              required
              className="h-14 w-full rounded-[20px] border border-slate-200 bg-white px-5 text-base text-slate-700 outline-none transition focus:border-primary focus:ring-2 focus:ring-primary/15"
              placeholder="Enter a new password"
            />
          </div>

          <div>
            <label className="mb-2 block text-[11px] font-bold uppercase tracking-[0.22em] text-slate-700">
              Confirm new password
            </label>
            <input
              type="password"
              value={confirmNewPassword}
              onChange={handleConfirmPasswordChange}
              required
              className="h-14 w-full rounded-[20px] border border-slate-200 bg-white px-5 text-base text-slate-700 outline-none transition focus:border-primary focus:ring-2 focus:ring-primary/15"
              placeholder="Confirm your new password"
            />
          </div>

          <p className="text-sm text-slate-500">
            Password must be at least 8 characters and include a number and
            special character.
          </p>

          {error ? <p className="text-sm text-red-500">{error}</p> : null}

          <button
            type="submit"
            disabled={loading}
            className="inline-flex h-14 w-full items-center justify-center rounded-full bg-primary px-6 text-base font-semibold text-white shadow-[0_12px_24px_rgba(0,128,128,0.2)] transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {loading ? "Updating..." : "Change Password"}
          </button>
        </form>
      </AuthShell>
    </>
  );
};

export default ChangePasswordWindow;
